import React from "react";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import DashboardSidebar from "../component/dash/DashboardSidebar";
import themeLarge from "../component/ThemeLarge";
import { showNotification } from "../uti";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from(
  { length: CURRENT_YEAR - 1990 + 1 },
  (_, i) => String(CURRENT_YEAR - i)
);

const MONTH_INDEX = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4,  Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

const emptyForm = {
  year:        String(CURRENT_YEAR),
  month:       "Jan",
  title:       "",
  description: "",
  url:         "",
};

class TimelineAdmin extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isSidebarOpen: false,
      entries:       [],
      loading:       true,
      saving:        false,
      form:          { ...emptyForm },
    };
  }

  componentDidMount() {
    this.fetchEntries();
  }

  toggleSidebar = () => {
    this.setState((prev) => ({ isSidebarOpen: !prev.isSidebarOpen }));
  };

  // ─── Firestore ────────────────────────────────────────────────────────────

  fetchEntries = async () => {
    try {
      const db   = getFirestore();
      const snap = await getDocs(collection(db, "timeline"));
      const entries = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          if (b.year !== a.year) return parseInt(b.year) - parseInt(a.year);
          return (MONTH_INDEX[b.month] ?? 0) - (MONTH_INDEX[a.month] ?? 0);
        });
      this.setState({ entries, loading: false });
    } catch (err) {
      console.error(err);
      showNotification("Failed to load timeline entries.", "error");
      this.setState({ loading: false });
    }
  };

  handleSave = async () => {
    const { form } = this.state;

    // ── validation ──
    if (!form.title.trim()) {
      showNotification("Title is required.", "error"); return;
    }
    if (form.title.trim().length > 80) {
      showNotification("Title must be 80 characters or fewer.", "error"); return;
    }
    if (!form.description.trim()) {
      showNotification("Description is required.", "error"); return;
    }
    if (form.description.trim().length > 300) {
      showNotification("Description must be 300 characters or fewer.", "error"); return;
    }
    if (form.url.trim()) {
      try { new URL(form.url.trim()); }
      catch { showNotification("URL must be a valid web address.", "error"); return; }
    }

    this.setState({ saving: true });
    try {
      const db = getFirestore();
      const payload = {
        year:        form.year,
        month:       form.month,
        title:       form.title.trim(),
        description: form.description.trim(),
        url:         form.url.trim(),
        createdAt:   serverTimestamp(),
      };
      const ref = await addDoc(collection(db, "timeline"), payload);
      const newEntry = { id: ref.id, ...payload };

      const entries = [newEntry, ...this.state.entries].sort((a, b) => {
        if (b.year !== a.year) return parseInt(b.year) - parseInt(a.year);
        return (MONTH_INDEX[b.month] ?? 0) - (MONTH_INDEX[a.month] ?? 0);
      });

      this.setState({ entries, form: { ...emptyForm }, saving: false });
      showNotification("Entry added!", "success");
    } catch (err) {
      console.error(err);
      showNotification("Failed to save entry.", "error");
      this.setState({ saving: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm("Delete this timeline entry?")) return;
    try {
      await deleteDoc(doc(getFirestore(), "timeline", id));
      this.setState((prev) => ({
        entries: prev.entries.filter((e) => e.id !== id),
      }));
      showNotification("Entry deleted.", "success");
    } catch (err) {
      console.error(err);
      showNotification("Failed to delete entry.", "error");
    }
  };

  // ─── form helpers ─────────────────────────────────────────────────────────

  setField = (field, value) => {
    this.setState((prev) => ({ form: { ...prev.form, [field]: value } }));
  };

  // ─── render ───────────────────────────────────────────────────────────────

  render() {
    const { isSidebarOpen, entries, loading, saving, form } = this.state;
    const T = themeLarge;

    return (
      <div style={{ display: "flex", height: "calc(100vh - 56px)", marginTop: "75px" }}>
        <DashboardSidebar isOpen={isSidebarOpen} onToggle={this.toggleSidebar} />

        <div
          className="container-fluid"
          style={{
            background: T.colors.bg,
            padding: "24px 32px",
            flexGrow: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* header */}
          <p style={{
            fontSize: T.fontSize.h1,
            fontFamily: "Noto Sans",
            color: T.colors.primary,
            fontWeight: 600,
            marginBottom: "20px",
          }}>
            Timeline Admin
            <span style={{ fontSize: T.fontSize.small, paddingLeft: "16px", fontWeight: 400 }}>
              {entries.length} {entries.length === 1 ? "entry" : "entries"}
            </span>
          </p>

          {/* two-column body */}
          <div className="row" style={{ flexGrow: 1, overflow: "hidden" }}>

            {/* ── LEFT: entries list ── */}
            <div
              className="col-md-5"
              style={{ height: "100%", overflowY: "auto", paddingRight: "16px" }}
            >
              {loading ? (
                <p style={{ color: T.colors.text }}>Loading…</p>
              ) : entries.length === 0 ? (
                <p style={{ color: "#888" }}>No entries yet. Add one on the right.</p>
              ) : (
                entries.map((entry) => (
                  <div
                    key={entry.id}
                    style={{
                      background: T.colors.secondary,
                      border: `1px solid ${T.colors.primary}30`,
                      borderRadius: "10px",
                      padding: "14px 16px",
                      marginBottom: "12px",
                      boxShadow: T.shadows.little,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <span style={{
                        fontSize: "1.3rem", fontWeight: "800",
                        color: T.colors.primary, fontFamily: "Noto Sans",
                      }}>
                        {entry.year}
                        <span style={{ fontSize: T.fontSize.small, fontWeight: 400, marginLeft: "8px", color: "#888" }}>
                          {entry.month}
                        </span>
                      </span>
                      <button
                        onClick={() => this.handleDelete(entry.id)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: T.colors.hover,
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          padding: "2px 6px",
                        }}
                      >
                        Delete
                      </button>
                    </div>

                    <p style={{
                      fontWeight: 600, fontSize: "0.95rem",
                      color: T.colors.text, margin: "6px 0 4px",
                    }}>
                      {entry.title}
                    </p>
                    <p style={{ fontSize: "0.82rem", color: "#555", margin: 0, lineHeight: 1.5 }}>
                      {entry.description}
                    </p>
                    {entry.url && (
                      <a
                        href={entry.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: "0.78rem", color: T.colors.primary, marginTop: "6px", display: "block" }}
                      >
                        {entry.url}
                      </a>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* ── RIGHT: add entry form ── */}
            <div
              className="col-md-7"
              style={{ height: "100%", overflowY: "auto", paddingLeft: "24px" }}
            >
              <div style={{
                background: T.colors.secondary,
                borderRadius: "12px",
                padding: "24px",
                border: `1px solid ${T.colors.primary}30`,
                boxShadow: T.shadows.little,
              }}>
                <p style={{
                  fontSize: T.fontSize.h3, fontFamily: "Noto Sans",
                  color: T.colors.primary, fontWeight: 600, marginBottom: "20px",
                }}>
                  Add New Entry
                </p>

                {/* year + month row */}
                <div className="row mb-3">
                  <div className="col-6">
                    <label style={labelStyle}>Year</label>
                    <select
                      className="form-select"
                      value={form.year}
                      onChange={(e) => this.setField("year", e.target.value)}
                      style={inputStyle}
                    >
                      {YEARS.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label style={labelStyle}>Month</label>
                    <select
                      className="form-select"
                      value={form.month}
                      onChange={(e) => this.setField("month", e.target.value)}
                      style={inputStyle}
                    >
                      {MONTHS.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* title */}
                <div className="mb-3">
                  <label style={labelStyle}>
                    Title
                    <span style={charCountStyle(form.title.length, 80)}>
                      {" "}{form.title.length}/80
                    </span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={80}
                    value={form.title}
                    onChange={(e) => this.setField("title", e.target.value)}
                    placeholder="What happened?"
                    style={inputStyle}
                  />
                </div>

                {/* description */}
                <div className="mb-3">
                  <label style={labelStyle}>
                    Description
                    <span style={charCountStyle(form.description.length, 300)}>
                      {" "}{form.description.length}/300
                    </span>
                  </label>
                  <textarea
                    className="form-control"
                    maxLength={300}
                    rows={4}
                    value={form.description}
                    onChange={(e) => this.setField("description", e.target.value)}
                    placeholder="A short description of the event…"
                    style={{ ...inputStyle, resize: "vertical" }}
                  />
                </div>

                {/* url */}
                <div className="mb-4">
                  <label style={labelStyle}>URL <span style={{ fontWeight: 400, color: "#888" }}>(optional)</span></label>
                  <input
                    type="url"
                    className="form-control"
                    value={form.url}
                    onChange={(e) => this.setField("url", e.target.value)}
                    placeholder="https://…"
                    style={inputStyle}
                  />
                </div>

                <button
                  onClick={this.handleSave}
                  disabled={saving}
                  style={{
                    background: T.colors.primary,
                    color: T.colors.secondary,
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 32px",
                    fontSize: T.fontSize.body,
                    fontFamily: "Kumbh Sans",
                    cursor: saving ? "not-allowed" : "pointer",
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? "Saving…" : "Add Entry"}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }
}

// ─── shared micro-styles ──────────────────────────────────────────────────────

const labelStyle = {
  display: "block",
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "#333",
  marginBottom: "6px",
  fontFamily: "Kumbh Sans",
};

const inputStyle = {
  fontFamily: "Kumbh Sans",
  fontSize: "0.9rem",
};

const charCountStyle = (current, max) => ({
  fontWeight: 400,
  color: current >= max ? "#e00" : "#999",
  fontSize: "0.78rem",
});

export default TimelineAdmin;
